// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { CodeSystemConcept } from '@medplum/fhirtypes';
import type {
  AppointmentMode,
  AppointmentTypeDefinition,
  PatientStatusCategory,
  ProviderRoleCode,
} from './appointment-types';
import { APPOINTMENT_TYPE_SYSTEM } from './appointment-types';

/**
 * Parse a CodeSystem concept's properties into an AppointmentTypeDefinition.
 * @param concept - The FHIR CodeSystem concept to parse
 * @returns The parsed appointment type definition
 */
export function parseAppointmentType(concept: CodeSystemConcept): AppointmentTypeDefinition {
  const props = concept.property ?? [];

  const getStringProp = (code: string): string => {
    const prop = props.find((p) => p.code === code);
    return prop?.valueString ?? '';
  };

  const getIntegerProp = (code: string): number => {
    const prop = props.find((p) => p.code === code);
    return prop?.valueInteger ?? 0;
  };

  const billingCodeFull = getStringProp('billingCode');
  const [billingCode, billingModifier] = billingCodeFull.includes('-')
    ? billingCodeFull.split('-')
    : [billingCodeFull, ''];

  const providerTypesStr = getStringProp('providerTypes');
  const providerTypes = providerTypesStr
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean) as ProviderRoleCode[];

  const preferredStr = getStringProp('preferredProvider');
  const preferredProvider = preferredStr ? (preferredStr as ProviderRoleCode) : undefined;
  const billingCodes = getStringProp('billingCodes')
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);

  let parsedBillingCodes: string[] = [];
  if (billingCodes.length > 0) {
    parsedBillingCodes = billingCodes;
  } else if (billingCode) {
    parsedBillingCodes = [billingCode];
  }

  return {
    coding: {
      system: APPOINTMENT_TYPE_SYSTEM,
      code: concept.code,
      display: concept.display,
    },
    display: concept.display ?? concept.code ?? '',
    durationMinutes: getIntegerProp('duration') || 15,
    billingCode,
    billingModifier,
    billingCodes: parsedBillingCodes,
    estimateCode: getStringProp('estimateCode') as AppointmentTypeDefinition['estimateCode'],
    providerTypes,
    preferredProvider,
    patientStatus: (getStringProp('patientStatus') || 'existing') as PatientStatusCategory,
    patientEligibility: getStringProp('patientEligibility'),
    mode: (getStringProp('mode') || 'virtual') as AppointmentMode,
    visibleInPortal: getStringProp('visibleInPortal') === 'true',
    placeOfServiceCode: getStringProp('placeOfServiceCode') || (getStringProp('mode') === 'in-person' ? '11' : '02'),
    schedulable: getStringProp('schedulable') !== 'false',
  };
}

/**
 * Parse an entire CodeSystem into an array of AppointmentTypeDefinitions.
 * @param concepts - The array of FHIR CodeSystem concepts to parse
 * @returns An array of parsed appointment type definitions
 */
export function parseAppointmentTypeCodeSystem(concepts: CodeSystemConcept[]): AppointmentTypeDefinition[] {
  return concepts.map(parseAppointmentType);
}

/**
 * Get provider labels with preference indicators for UI display.
 * @param apptType - The appointment type definition to get labels for
 * @returns An array of provider role labels with preference indicators
 */
export function getProviderTypeLabels(
  apptType: AppointmentTypeDefinition
): { role: ProviderRoleCode; isPreferred: boolean; label: string }[] {
  return apptType.providerTypes.map((role) => ({
    role,
    isPreferred: role === apptType.preferredProvider,
    label: role === apptType.preferredProvider ? `${role} (preferred)` : role,
  }));
}
